/**
 * AssignTeamDialog — add/remove a platform user's team memberships.
 * Extracted from `routes/admin/users/index.tsx` (issue #1411).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { addTeamMember, removeTeamMember } from '../../lib/apiClient';
import { sortTeamsByName } from '../../lib/usersFilter';
import type { PlatformUser } from '../../types/users';
import type { Team } from '../../graphql/types';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';

export interface AssignTeamDialogProps {
  open: boolean;
  user: PlatformUser | null;
  allTeams: Team[];
  userTeams: Team[];
  onClose: () => void;
  onMutate: () => Promise<void>;
}

export function AssignTeamDialog({
  open,
  user,
  allTeams,
  userTeams,
  onClose,
  onMutate,
}: AssignTeamDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const titleId = useId();
  const [filter, setFilter] = useState('');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.open = open;
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFilter('');
      setError(null);
      setPendingUserId(null);
    }
  }, [open]);

  const userTeamIds = useMemo(() => new Set(userTeams.map((t) => t.entityId)), [userTeams]);

  // Sort alphabetically by team name (issue #1224) — both lists previously
  // rendered in raw GraphQL connection / membership order.
  const sortedUserTeams = useMemo(() => sortTeamsByName(userTeams), [userTeams]);

  const filteredAvailable = useMemo(() => {
    const q = filter.toLowerCase();
    const matches = allTeams.filter(
      (t) => !userTeamIds.has(t.entityId) && t.name.toLowerCase().includes(q)
    );
    return sortTeamsByName(matches);
  }, [allTeams, userTeamIds, filter]);

  const handleAdd = async (teamId: string): Promise<void> => {
    if (!user) return;
    setPendingUserId(teamId);
    setError(null);
    try {
      await addTeamMember(teamId, user.userId);
      await onMutate();
    } catch {
      setError('Failed to assign team. Please try again.');
    } finally {
      setPendingUserId(null);
    }
  };

  const handleRemove = async (teamId: string): Promise<void> => {
    if (!user) return;
    setPendingUserId(teamId);
    setError(null);
    try {
      await removeTeamMember(teamId, user.userId);
      await onMutate();
    } catch {
      setError('Failed to remove team. Please try again.');
    } finally {
      setPendingUserId(null);
    }
  };

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  return (
    <DialogElement
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby={titleId}
      className="users-dialog users-assign-dialog"
      data-testid="assign-team-dialog"
    >
      <div className="users-dialog-content">
        <div className="users-dialog-header">
          <h2 id={titleId} className="forge-typography--headline5">
            Assign Teams
          </h2>
          {user && <p className="users-dialog-subtitle">{user.email}</p>}
        </div>
        <div className="users-dialog-body">
          {error && (
            <p className="users-field-error" role="alert">
              {error}
            </p>
          )}

          {sortedUserTeams.length > 0 && (
            <div className="users-assign-section">
              <span className="forge-typography--label1">Current teams</span>
              <ul className="users-assign-current-list" aria-label="Current team assignments">
                {sortedUserTeams.map((t) => (
                  <li key={t.entityId} className="users-assign-current-item">
                    <span>{t.name}</span>
                    <ForgeButton
                      type="button"
                      variant="text"
                      className="users-assign-remove-btn"
                      disabled={pendingUserId === t.entityId}
                      onClick={() => void handleRemove(t.entityId)}
                      aria-label={`Remove from ${t.name}`}
                      data-testid={`remove-from-team-${t.entityId}`}
                    >
                      ✕
                    </ForgeButton>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="users-assign-section">
            <label htmlFor="team-filter" className="forge-typography--label1">
              Add to team
            </label>
            <input
              id="team-filter"
              type="text"
              className="users-text-input"
              placeholder="Filter by name…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="team-filter-input"
            />
            {filteredAvailable.length === 0 ? (
              <p className="users-assign-empty">
                {filter ? 'No matching teams.' : 'User belongs to all available teams.'}
              </p>
            ) : (
              <ul className="users-assign-list" aria-label="Available teams">
                {filteredAvailable.map((t) => (
                  <li key={t.entityId} className="users-assign-item">
                    <div>
                      <span className="users-assign-team-name">{t.name}</span>
                    </div>
                    <ForgeButton
                      type="button"
                      variant="outlined"
                      disabled={pendingUserId === t.entityId}
                      onClick={() => void handleAdd(t.entityId)}
                      aria-label={`Assign to ${t.name}`}
                      data-testid={`add-to-team-${t.entityId}`}
                    >
                      {pendingUserId === t.entityId ? 'Assigning…' : 'Assign'}
                    </ForgeButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="users-dialog-actions">
          <ForgeButton type="button" variant="outlined" onClick={onClose}>
            Close
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
